import { useId } from "react";
import { setFilters, useFilters } from "@/state/filters.ts";
import { formatMinutes } from "../entry/RuntimeChip.tsx";
import styles from "./controls.module.css";

/**
 * A ceiling on what any single stop may cost you — "nothing longer than two
 * hours tonight". It bites hardest on seasons, which is the point.
 *
 * The slider walks a list of sane stops rather than raw minutes: 240 discrete
 * values would be a worse control, and the URL still carries plain minutes so
 * the param stays readable and future stops don't invalidate old links.
 */
const STOPS: (number | null)[] = [45, 60, 75, 90, 105, 120, 150, 180, 240, 360, null];

/** Nearest stop for an arbitrary minute value out of a URL. */
function stopIndex(minutes: number | null): number {
  if (minutes === null) return STOPS.length - 1;
  let best = 0;
  for (let i = 0; i < STOPS.length; i++) {
    const stop = STOPS[i];
    if (stop === null || stop === undefined) continue;
    const bestStop = STOPS[best];
    if (bestStop === null || bestStop === undefined) {
      best = i;
      continue;
    }
    if (Math.abs(stop - minutes) < Math.abs(bestStop - minutes)) best = i;
  }
  return best;
}

export function RuntimeFilter() {
  const filters = useFilters();
  const id = useId();

  const index = stopIndex(filters.maxMinutes);
  const label =
    filters.maxMinutes === null ? "No cap" : `Under ${formatMinutes(filters.maxMinutes)}`;

  return (
    <div className={styles.field}>
      <div className={styles.head}>
        <label className={styles.label} htmlFor={id}>
          Longest single stop
        </label>
        <output className={styles.value} htmlFor={id}>
          {label}
        </output>
      </div>
      <input
        id={id}
        className={styles.range}
        type="range"
        min={0}
        max={STOPS.length - 1}
        step={1}
        value={index}
        aria-valuetext={label}
        onChange={(event) =>
          setFilters((prev) => ({
            ...prev,
            maxMinutes: STOPS[Number(event.target.value)] ?? null,
          }))
        }
      />
      <p className={styles.hint}>
        {filters.useAlternatives
          ? "Measured against the shortest curated route for each stop."
          : "Measured against full runtime: a 12-hour season counts as one 12-hour stop."}
      </p>
    </div>
  );
}
